import { useQuery } from "@apollo/client";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { Button, Checkbox, Select } from "@aqar-app/react-ui";
import { Icon } from "@/components/icon";

import css from "./.module.scss";
import {
  GET_ALL_CITIES,
  GET_ALL_DIRECTIONS,
  GET_ALL_DISTRICTS,
} from "@/graphql/queries/regions";
import styles from "../../containers/layouts/searchbar/searchbar.module.scss";
import { usePath } from "@/contexts/path/path";
import { FiltersUi } from "./controls";
import { DEFAULT_CATEGORY } from "@/lib/path-resolver-middlewares/category";
import { AppLink } from "../new-resolver/app.ink";
import { appHistory, appLocation } from "@/lib/app.history";
import {
  AqarPlusFilters,
  AqarPlusNotSubscribed,
} from "@/src/components/filters/aqarPlus/plus.filters";
import { useAuthState } from "@/src/contexts/auth/auth.provider";
import { useRouter } from "next/router";
import {
  trackApplySearch,
  trackCategoryChange,
  trackGenericCTAClickEvent,
  trackLocationChange,
  trackResetSearch,
  trackSetSearchParam,
} from "@/lib/tracking/trackNavigationEvents";
import { useAppState } from "@/contexts/app/app.provider";

export function Filters({ setOpened, opened } = {}) {
  const { user } = useAuthState();
  const counterRef = useRef();
  const {
    updateContext,
    filters,
    searchFields,
    category,
    district,
    direction,
    city,
    listing,
    categories: sourceCategories,
    basePath = "",
    listingType,
  } = usePath();
  const { listings } = useAppState();
  const categories = useMemo(() => {
    if (
      basePath?.includes("/daily-rent") ||
      basePath?.includes("/الحجز-اليومي")
    )
      return sourceCategories?.filter((t) => !t.id || t.id > 100);
    return sourceCategories?.filter((t) => t.id < 101);
  }, [sourceCategories, basePath]);

  const router = useRouter();

  const Fields = useMemo(() => {
    const filteredSearchFieldsByCity = searchFields?.filter((field) => {
      if (!field.citySpecific) return true;
      return field.citySpecific && field.cityId === city?.id;
    });
    return (
      filteredSearchFieldsByCity?.map((field) => [
        FiltersUi[field.type],
        field,
      ]) || []
    );
  }, [category, searchFields, city?.id]);

  const { data: { Web: { cities = [] } = {} } = {} } = useQuery(
    GET_ALL_CITIES,
    {
      variables: {
        category: category?.id || 0,
      },
    },
  );

  const { data: { Web: { directions = [] } = {} } = {} } = useQuery(
    GET_ALL_DIRECTIONS,
    {
      skip: !city || !city.directions,
      variables: {
        category: category?.id || 0,
        city_id: city?.id || 0,
      },
    },
  );
  const { data: { Web: { districts = [] } = {} } = {} } = useQuery(
    GET_ALL_DISTRICTS,
    {
      skip: !city || (city.directions && !direction),
      variables: {
        category: category?.id || 0,
        city_id: city?.city_id || city?.id || 0,
        ...(direction ? { direction_id: direction.direction_id } : {}),
      },
    },
  );
  useEffect(() => {
    if (opened) {
      document.body.style.height = "100vh";
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.height = "auto";
      document.body.style.overflow = "unset";
    };
  }, [opened]);

  return (
    <div className={css.filters}>
      <div className={css.header}>
        <Icon
          width={25}
          height={25}
          onClick={() => {
            appHistory.pushState({}, "", appLocation.href?.split("#")[0] || "");
            setOpened(false);
          }}
          className={css.headerIcon}
          name="OutlinedClose"
        />
        <div className={styles.title}>ابحث عن عقار</div>
        <Icon
          width={25}
          height={25}
          className={css.headerIcon}
          name="reload"
          onClick={() => {
            updateContext({
              filters: { published: { eq: 1 } },
              listing: null,
              page: 0,
              polygon: undefined,
            });
            trackResetSearch(listingType, category?.en);
          }}
        />
      </div>

      <div className={css.body}>
        {user?.show_aqar_plus ? (
          user?.active_aqar_plus_user ? (
            <AqarPlusFilters />
          ) : (
            <AqarPlusNotSubscribed />
          )
        ) : null}
        <div>
          <h3>قسم العقار</h3>
          <Select
            onSelect={(id, event) => {
              const di = event.raw;
              if (!di) return;

              trackCategoryChange(listingType, di?.en, "search");

              const {
                owner,
                verified_owner,
                unverified_owner,
                direct_from_owners,
                unique_listing,
                keyword,
                create_time,
              } = filters;

              let newFilters = {
                category: di || DEFAULT_CATEGORY,
                page: 0,
                filters: {
                  unpublished: filters.unpublished,
                  owner,
                  verified_owner,
                  unverified_owner,
                  direct_from_owners,
                  unique_listing,
                  keyword,
                  create_time,
                },
              };
              if (listing) {
                return updateContext({
                  ...newFilters,
                  listing: null,
                });
              }

              updateContext(newFilters);
            }}
            searchable
            value={category?.id || 0}
          >
            {categories?.map((cat) => (
              <Select.Option raw={cat} key={cat.id} value={cat.id}>
                <p>{cat.id ? cat.name : "عقارات"}</p>
              </Select.Option>
            ))}
          </Select>
        </div>

        <div>
          <h3>المدينة</h3>
          <Select
            onOpen={() => {
              trackGenericCTAClickEvent("change location", {
                listing_type: listingType,
                property_category: category?.en,
              });
            }}
            onSelect={(id, event) => {
              const di = event.raw;

              // if previous city was riyadh and new city is not riyadh, reset all filters because riyadh has specific filters (near bus and metro)
              if (city?.id === 21 && di?.city_id !== 21) {
                const {
                  owner,
                  verified_owner,
                  unverified_owner,
                  direct_from_owners,
                  unique_listing,
                  keyword,
                  create_time,
                } = filters;

                updateContext({
                  category: category || DEFAULT_CATEGORY,
                  city: di
                    ? {
                        ...di,
                        id: di.city_id,
                      }
                    : null,
                  district: null,
                  direction: null,
                  listing: undefined,
                  page: 0,
                  filters: {
                    unpublished: filters.unpublished,
                    owner,
                    verified_owner,
                    unverified_owner,
                    direct_from_owners,
                    unique_listing,
                    keyword,
                    create_time,
                  },
                });
              } else {
                updateContext({
                  category: category || DEFAULT_CATEGORY,
                  city: di
                    ? {
                        ...di,
                        id: di.city_id,
                      }
                    : null,
                  district: null,
                  direction: null,
                  listing: undefined,
                  page: 0,
                });
              }

              trackLocationChange(listingType, category?.en, di?.name_en);
            }}
            value={city?.id}
            searchable
          >
            <Select.Option>
              <p>غير محدد</p>
            </Select.Option>
            {cities?.map((ct) => (
              <Select.Option raw={ct} key={ct.city_id} value={ct.city_id}>
                <p>{ct.name}</p>
              </Select.Option>
            ))}
          </Select>
        </div>

        {city?.directions && (
          <div>
            <h3>الاتجاه</h3>
            <Select
              onOpen={() => {
                trackGenericCTAClickEvent("change location", {
                  listing_type: listingType,
                  property_category: category?.en,
                });
              }}
              onSelect={(id, event) => {
                const di = event.raw;
                updateContext({
                  direction: di
                    ? {
                        ...di,
                        id: di.direction_id,
                      }
                    : null,
                  district: null,
                  listing: undefined,
                  page: 0,
                });
                trackLocationChange(listingType, category?.en, di?.name_en);
              }}
              searchable
              value={direction?.id}
            >
              <Select.Option>
                <p>غير محدد</p>
              </Select.Option>
              {directions?.map((dir) => (
                <Select.Option
                  raw={dir}
                  key={dir.direction_id}
                  value={dir.direction_id}
                >
                  <p>{dir.name}</p>
                </Select.Option>
              ))}
            </Select>
          </div>
        )}

        {((city && !city.directions) || (city && direction)) && (
          <div>
            <h3>الحي</h3>
            <Select
              onOpen={() => {
                trackGenericCTAClickEvent("change location", {
                  listing_type: listingType,
                  property_category: category?.en,
                });
              }}
              onSelect={(id, event) => {
                const di = event.raw;
                updateContext({
                  listing: undefined,
                  page: 0,

                  district: di
                    ? {
                        ...di,
                        id: di.district_id,
                      }
                    : null,
                });
                trackLocationChange(listingType, category?.en, di?.name_en);
              }}
              searchable
              value={district?.id}
            >
              <Select.Option>
                <p>غير محدد</p>
              </Select.Option>
              {districts?.map((distr) => (
                <Select.Option
                  raw={distr}
                  key={distr.district_id}
                  value={distr.district_id}
                >
                  <p
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {distr.name}
                  </p>
                </Select.Option>
              ))}
            </Select>
          </div>
        )}

        {user?.paid > 0 && category && !basePath?.includes("daily-rent") && (
          <div>
            <Checkbox
              switch
              checked={!!filters?.unpublished}
              onChange={() => {
                const unpublished = !filters?.unpublished
                  ? { eq: 1 }
                  : undefined;
                if (unpublished)
                  return updateContext({
                    filters: { ...filters, unpublished, published: { eq: 0 } },
                    listing: undefined,
                  });

                updateContext({
                  filters: { ...filters, unpublished, published: undefined },
                  listing: undefined,
                  district: null,
                  page: 0,
                });
              }}
            >
              <p>عرض طلبات التسويق</p>
            </Checkbox>
          </div>
        )}

        {Fields.map(([Component, props]) => (
          <Component
            key={props.name}
            {...props}
            value={
              filters?.[props?.name]?.[props?.operation] ||
              filters?.[props?.name]
            }
            onChange={(name, filter) => {
              trackSetSearchParam(
                listingType,
                category?.en,
                name,
                filter,
                filters,
              );
              if (listing) {
                updateContext({
                  filters: { ...filters, [name]: filter },
                  listing: null,
                  district: null,
                  page: 0,
                });

                return;
              }

              updateContext({
                filters: { ...filters, [name]: filter },
                page: 0,
              });
            }}
            {...(props?.name === "villa_type" && {
              radioOptions: { style: { fontSize: "12px" } },
            })}
          />
        ))}
      </div>
      <div className={css.search}>
        <Button
          theme="primary"
          onClick={() => {
            trackApplySearch(
              listingType,
              category?.en,
              {
                city_name: city?.name_en,
                direction_name: direction?.name_en,
                district_name: district?.name_en,
                ...filters,
              },
              listings?.total,
            );
            router.push(
              appLocation.pathname +
                "" +
                decodeURIComponent(appLocation.search),
            );
            setOpened(false);
          }}
        >
          بحث
          {listings?.total ? ` ${listings.total} إعلان ` : ""}
        </Button>
      </div>
    </div>
  );
}

export function MainTabs() {
  const { basePath, updateContext } = usePath();
  return (
    <div className={css.tabs}>
      <AppLink
        aria-label="الرئيسية"
        onClick={() => {
          let base = "/";
          if (basePath?.includes("/map")) base = "/map/عقارات";
          updateContext({
            basePath: base,
            filters: {},
            homeLabel: "الرئيسية",
            listing: null,
            mode: undefined,
            category: DEFAULT_CATEGORY,
            city: null,
            direction: null,
            district: null,
            page: 0,
            fields: [],

            daily_renting_filter: undefined,
            polygon: undefined,
            extendedFields: [],
            searchFields: [],
            quickFields: [],
          });
        }}
        className={
          (!basePath ||
            (!basePath?.includes("daily") &&
              !basePath?.includes("الحجز-اليومي"))) &&
          !basePath?.includes("المشاريع") &&
          !basePath?.includes("projects") &&
          css.active
        }
        path="/"
      >
        <Icon
          width={15}
          height={15}
          name={
            (!basePath ||
              (!basePath?.includes("daily") &&
                !basePath?.includes("الحجز-اليومي"))) &&
            !basePath?.includes("المشاريع") &&
            !basePath?.includes("projects")
              ? "v1/building"
              : "v1/building-gray"
          }
        />
        العقارات
      </AppLink>
      <AppLink
        aria-label="المشاريع"
        onClick={() => {
          let base = "/المشاريع-العقارية";
          updateContext({
            basePath: base,
            homeLabel: "المشاريع العقارية",
            mode: "red",
            filters: {},
            listing: null,
            city: null,
            direction: null,
            district: null,
            daily_renting_filter: undefined,
            category: DEFAULT_CATEGORY,
            page: 0,
            fields: [],
            extendedFields: [],
            searchFields: [],
          });
          // trackDailyRentMode();
        }}
        className={
          (basePath?.includes("/projects") ||
            basePath?.includes("/المشاريع")) &&
          css.active
        }
        path="/المشاريع-العقارية"
      >
        <Icon
          width={15}
          height={15}
          name={
            basePath?.includes("/projects") || basePath?.includes("/المشاريع")
              ? "v1/buildings-brand-v1"
              : "v1/buildings"
          }
        />
        المشاريع
      </AppLink>
      <AppLink
        aria-label="الإيجار اليومي"
        onClick={() => {
          let base = "/الحجز-اليومي";
          if (basePath?.includes("/map")) base = "/map/الحجز-اليومي";
          updateContext({
            basePath: base,
            homeLabel: "الإيجار اليومي",
            filters: {},
            listing: null,
            city: null,
            direction: null,
            district: null,
            daily_renting_filter: {
              availability: { eq: 1 },
            },
            category: DEFAULT_CATEGORY,
            page: 0,
            fields: [],
            extendedFields: [],
            searchFields: [],
          });
          // trackDailyRentMode();
        }}
        className={
          (basePath?.includes("/daily-rent") ||
            basePath?.includes("/الحجز-اليومي")) &&
          css.active
        }
        path="/الحجز-اليومي"
      >
        <Icon
          width={15}
          height={15}
          name={
            basePath?.includes("/daily-rent") ||
            basePath?.includes("/الحجز-اليومي")
              ? "v1/calendar-daily-brand-v1"
              : "v1/calendar-daily"
          }
        />
        الإيجار اليومي
      </AppLink>
    </div>
  );
}

Filters.Layout = function Layout({ children } = {}) {
  const [opened, setOpened] = useState(false);
  const router = useRouter();
  const { listing, basePath, updateContext } = usePath();

  useEffect(() => {
    function open() {
      setOpened(true);
    }

    document.addEventListener("opensearchbar", open);

    return () => {
      document.removeEventListener("opensearchbar", open);
    };
  }, [opened]);
  useEffect(() => {
    if (appLocation.hash === "#filters") setOpened(true);
  }, []);
  useEffect(() => {
    const onpopstate = () => {
      /*
       * When user clicks back we will query localStorage for the resolvedPath
       * */
      if (appLocation.hash !== "#filters" && opened) setOpened(false);

      if (appLocation.hash === "#filters" && !opened) setOpened(true);
    };
    window.addEventListener("popstate", onpopstate);

    return () => window.removeEventListener("popstate", onpopstate);
  }, [opened]);
  return (
    <>
      {opened && (
        <>
          <div className={css.sidebar}>
            <div
              onClick={() => {
                router.push(
                  appLocation.pathname +
                    "" +
                    decodeURIComponent(appLocation.search),
                );
                setOpened(false);
              }}
              className={css.outsidesidebar}
            />
            <Filters setOpened={setOpened} opened={opened} />
          </div>
        </>
      )}

      <div className={css.root}>
        <div
          className={`${css.listingsRoot} ${listing ? css.white : ""} ${
            !listing && css.list
          }`}
        >
          <MainTabs />
          {children}
        </div>
      </div>
    </>
  );
};
export default Filters;
